/**
 * Asset Path Utility
 * Provides consistent access to asset URLs with Shopify CDN awareness
 * Handles both development and production environments
 */

/**
 * Get the correct asset path prefixed with the CDN URL if needed
 * @param {string} assetPath - The asset filename or path (e.g. 'bg-diagonal-lines.png')
 * @returns {string} - The fully qualified asset URL
 */
export function getAssetPath(assetPath) {
  // More robust asset URL handling for development and production
  let assetUrl = '';
  const maybourne = window.maybourne || {};
  
  // Check if we're in local development with Shopify CLI (usually :9292 or similar port)
  const isLocalDev = window.location.port === '9292' || 
                    window.location.hostname === '127.0.0.1' ||
                    window.location.hostname === 'localhost';
  
  if (isLocalDev) {
    // In local development, use relative paths to assets in the theme
    assetUrl = '/assets';
  } else {
    // In production, use the Shopify CDN URL
    assetUrl = maybourne.asset_url || 
              (window.theme && window.theme.asset_url) || 
              '/cdn/shop/assets';
  }
  
  // Ensure assetPath doesn't start with a slash when we're concatenating
  if (assetPath.startsWith('/')) {
    assetPath = assetPath.substring(1);
  }
  
  // Fix any double slashes in the URL construction
  return `${assetUrl}${assetUrl.endsWith('/') ? '' : '/'}${assetPath}`;
}

/**
 * Test if an image can be loaded
 * @param {string} url - URL to test
 * @returns {Promise<boolean>} - Whether the image loaded successfully
 */
export function testImageLoad(url) {
  return new Promise(resolve => {
    // Set a timeout to handle extremely slow loading or hanging requests
    const timeout = setTimeout(() => {
      console.warn(`Image load timed out for: ${url}`);
      resolve(false);
    }, 5000); // 5 second timeout
    
    const img = new Image();
    img.onload = () => {
      clearTimeout(timeout);
      console.log(`Successfully loaded image: ${url}`);
      resolve(true);
    };
    img.onerror = (err) => {
      clearTimeout(timeout);
      console.warn(`Failed to load image: ${url}`, err);
      resolve(false);
    };
    img.src = url;
  });
} 